import { VideoPlayerScreenErrorCode } from '../../shared/video-player/models/video-player-screen-error-code.model'
import { LiveEventService } from '../services/event.service'
import { PlatformService } from '../../singleton-services/platform.service'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { PreviewService } from '../services/preview.service'
import { Observable, of } from 'rxjs'
import { catchError, map, delay, switchMap } from 'rxjs/operators'
import { HttpErrorResponse } from '@angular/common/http'
import { retryBackoff } from '../../shared/rx-pipes/retry-backoff'
import { HlsLoaderResponse, HlsLoader } from '../components/video-player/video-player.interfaces'
import { InjectionToken } from '@angular/core'
import { VideoPlayerMeta } from '../models/stream-tile'
import { AuthenticatedStreamResponse } from '@flocasts/flosports30-types/dist/live-api'
import { LiveEventStatus } from 'src/app/events/events.enum'

export type HlsDelayInitFn = (min: number, max: number) => number

export const HLS_LOADER_INIT_DELAY_MIN = new InjectionToken<number>('hls.ld.init.min')
export const HLS_LOADER_INIT_DELAY_MAX = new InjectionToken<number>('hls.ld.init.max')
export const HLS_LOADER_INIT_DELAY_FN = new InjectionToken<HlsDelayInitFn>('hls.ld.init.fn')
export const HLS_LOADER_RETRY_INITIAL_INTERVAL = new InjectionToken<number>('hls.ld.retry.initial')
export const HLS_LOADER_RETRY_MAX_ATTEMPTS = new InjectionToken<number>('hls.ld.retry.max')

export function DEFAULT_HLS_LOADER_INIT_DELAY_FN() {
  return function hlsLoaderInitDelayFn(min: number, max: number) {
    const _min = Math.ceil(min)
    return Math.floor(Math.random() * (Math.floor(max) - _min)) + _min
  }
}

const mapToHlsOutputInterface = (
  token: AuthenticatedStreamResponse
): HlsLoaderResponse<AuthenticatedStreamResponse> => {
  return {
    success: {
      hlsUrl: token.adTech === 'ssai' ? token.uri : token.cleanUri,
      meta: token
    }
  }
}

const mapSuccess = (source: Observable<AuthenticatedStreamResponse>) => source.pipe(map(mapToHlsOutputInterface))

const mapServiceErrorsToVideoErrors = (serviceErrorCode: number) => {
  switch (serviceErrorCode) {
    case 401:
      return VideoPlayerScreenErrorCode.AUTHENTICATION_REQUIRED
    case 402:
      return VideoPlayerScreenErrorCode.UNAUTHORIZED
    case 403:
      return VideoPlayerScreenErrorCode.FORBIDDEN
    case 404:
      return VideoPlayerScreenErrorCode.STREAM_NOT_FOUND
    case 422:
      return VideoPlayerScreenErrorCode.STREAM_NOT_FOUND
    case 451:
      return VideoPlayerScreenErrorCode.GEO_UNAVAILABLE
    default:
      return VideoPlayerScreenErrorCode.UNKNOWN_ERROR
  }
}

function returnCode<T>(code: VideoPlayerScreenErrorCode, meta?: VideoPlayerMeta) {
  return meta ? { code, meta } : { code }
}

function returnUnknownError() {
  return returnCode(VideoPlayerScreenErrorCode.UNKNOWN_ERROR)
}

export type HlsLoaderFactory = (
  es: LiveEventService,
  ps: PlatformService,
  auth: AuthService,
  prev: PreviewService,
  delayFn: HlsDelayInitFn,
  delayMin?: number,
  delayMax?: number,
  initialInterval?: number,
  maxRetries?: number
) => HlsLoader

export const hlsLoaderFactory: HlsLoaderFactory = (
  es: LiveEventService,
  ps: PlatformService,
  auth: AuthService,
  _prev: PreviewService,
  delayFn: HlsDelayInitFn,
  delayMin = 0,
  delayMax = 0,
  initialInterval = 0,
  maxRetries = 0
): HlsLoader<VideoPlayerMeta> => {
  return (videoReferenceId, isFreeStream, meta) => {
    const eventStatus = meta?.eventStatus
    const okToLoad = eventStatus !== LiveEventStatus.PreAir ? eventStatus : false

    return ps.isServer
      ? of({
          error: {
            code: -1
          }
        })
      : okToLoad
      ? auth.isPremium$.pipe(
          delay(delayFn(delayMin, delayMax)),
          switchMap(isPremiumUser =>
            isPremiumUser || isFreeStream
              ? es.getAuthenticatedStream(videoReferenceId).pipe(mapSuccess)
              : // NOTE: For the FLO-10852 Sport Data in Stream Tiles experiment, we had to comment this out.
                // We are opting to show a paywall immediately to anon/free users instead of a preview.
                // : prev.summary(videoReferenceId).canPreview
                // ? es
                // .getAuthenticatedPreviewStream(videoReferenceId)
                // .pipe(mapSuccess)
                of({
                  error: {
                    code: VideoPlayerScreenErrorCode.UNAUTHORIZED
                  }
                })
          ),
          retryBackoff({ initialInterval, maxRetries }),
          catchError((err: HttpErrorResponse) => {
            return err.status === 0
              ? of({
                  error: { code: err.status, message: err.statusText }
                })
              : of({
                  error: {
                    code: mapServiceErrorsToVideoErrors(err.status),
                    message: err.error.message || err.error.error.message
                  }
                })
          })
        )
      : of({
          error: returnError(meta)
        })
  }
}

const returnError = (meta: VideoPlayerMeta) => {
  const eventStatus = meta?.eventStatus

  switch (eventStatus) {
    case LiveEventStatus.PreAir:
      return meta.eventStartAt ? returnCode(VideoPlayerScreenErrorCode.PRE_AIR, meta) : returnUnknownError()
    case LiveEventStatus.Concluded:
      return { code: VideoPlayerScreenErrorCode.CONCLUDED }
    default:
      return returnUnknownError()
  }
}
